import type { Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';

/**
 * Resolves the Stripe instance for the payment modal.
 *
 * If set, Stripe.js is loaded from `stripeBaseUrl`, e.g. the Viatel Mock API's
 * stand-in, instead of the hard-coded default (js.stripe.com)
 */
export const getStripe = (publishableKey: string, stripeBaseUrl?: string): Promise<Stripe | null> =>
  stripeBaseUrl ? loadStripeFrom(stripeBaseUrl, publishableKey) : loadStripe(publishableKey);

const scripts = new Map<string, Promise<void>>();

const loadScript = (src: string) => {
  const loading = scripts.get(src);
  if (loading) return loading;

  const loaded = new Promise<void>((resolve, reject) => {
    // Set already if a previous script tag survived a hot reload that emptied the map.
    if (window.Stripe) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => reject(new Error(`Could not load Stripe.js from ${src}`)));

    document.head.appendChild(script);
  });

  scripts.set(src, loaded);
  return loaded;
};

const loadStripeFrom = async (stripeBaseUrl: string, publishableKey: string): Promise<Stripe | null> => {
  await loadScript(`${stripeBaseUrl.replace(/\/+$/, '')}/v3/index.js`);

  if (!window.Stripe) throw new Error(`The script at ${stripeBaseUrl} did not publish a Stripe global.`);

  return window.Stripe(publishableKey);
};
